import { useState, useEffect, useRef, useCallback } from "react";
import { Volume2, VolumeX, Radio, Calendar, Repeat } from "lucide-react";

/**
 * 24/7 Playlist Player
 * Behaves like a TV channel - always mid-stream, never stops.
 * Calculates what should be playing right now based on wall-clock time.
 * User can only control volume. Nothing else.
 */
export const PlaylistPlayer = ({
  playlistItems = [],
  currentIndex = 0,
  shuffle = false,
  loop = true,
  isLiveOverriding = false,
  fallbackVideoUrl = "",
  onIndexChange,
  autoPlay = true,
  scheduleEnabled = false,
  scheduleItems = [],
  fillGapsWithLoop = true
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [startOffset, setStartOffset] = useState(0); // seconds into the current video
  const [hasUserUnmuted, setHasUserUnmuted] = useState(false);
  const [scheduledContent, setScheduledContent] = useState(null);
  const [isInScheduledSlot, setIsInScheduledSlot] = useState(false);
  const [, forceRender] = useState(0); // for mute icon update only

  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const mutedRef = useRef(true); // track muted via ref to avoid re-renders
  const initDone = useRef(false);

  // Estimate default duration per video (YouTube videos don't expose duration without API)
  const DEFAULT_DURATION = 240; // 4 minutes assumed per video

  // Calculate which video should be playing right now based on wall-clock time
  useEffect(() => {
    if (initDone.current || playlistItems.length === 0) return;
    initDone.current = true;

    const totalDuration = playlistItems.length * DEFAULT_DURATION;
    // Use seconds since midnight as our position in the loop
    const now = new Date();
    const secondsSinceMidnight = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    const positionInLoop = secondsSinceMidnight % totalDuration;

    let accumulated = 0;
    for (let i = 0; i < playlistItems.length; i++) {
      const dur = DEFAULT_DURATION;
      if (accumulated + dur > positionInLoop) {
        setActiveIndex(i);
        setStartOffset(Math.floor(positionInLoop - accumulated));
        onIndexChange?.(i);
        return;
      }
      accumulated += dur;
    }
    // Fallback
    setActiveIndex(0);
    setStartOffset(0);
  }, [playlistItems.length, onIndexChange, DEFAULT_DURATION]);

  // Scheduled content check
  useEffect(() => {
    if (!scheduleEnabled || scheduleItems.length === 0) {
      setScheduledContent(null);
      setIsInScheduledSlot(false);
      return;
    }

    const checkSchedule = () => {
      const now = new Date();
      const currentDay = (now.getDay() + 6) % 7;
      const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

      const activeSlot = scheduleItems.find(slot => {
        const slotMatchesDay = slot.day === currentDay || slot.repeat === "daily";
        if (!slotMatchesDay) return false;
        const slotStart = slot.start_hour * 60 + (slot.start_minute || 0);
        const slotEnd = slot.end_hour * 60 + (slot.end_minute || 0);
        return currentTimeInMinutes >= slotStart && currentTimeInMinutes < slotEnd;
      });

      if (activeSlot) {
        setIsInScheduledSlot(true);
        if (activeSlot.content_type === "playlist_item" && playlistItems[activeSlot.content_index]) {
          setScheduledContent({ ...playlistItems[activeSlot.content_index], slotTitle: activeSlot.title });
        } else if (activeSlot.content_type === "custom_url" && activeSlot.content_url) {
          setScheduledContent({
            type: activeSlot.content_url.includes("youtube") ? "youtube" : "mp4",
            url: activeSlot.content_url, title: activeSlot.title, slotTitle: activeSlot.title
          });
        } else {
          setScheduledContent(null);
          setIsInScheduledSlot(false);
        }
      } else {
        setScheduledContent(null);
        setIsInScheduledSlot(false);
      }
    };

    checkSchedule();
    const interval = setInterval(checkSchedule, 60000);
    return () => clearInterval(interval);
  }, [scheduleEnabled, scheduleItems, playlistItems]);

  const getCurrentContent = useCallback(() => {
    if (scheduleEnabled && isInScheduledSlot && scheduledContent) return scheduledContent;
    if (scheduleEnabled && !isInScheduledSlot && !fillGapsWithLoop) return null;
    return playlistItems[activeIndex];
  }, [scheduleEnabled, isInScheduledSlot, scheduledContent, fillGapsWithLoop, playlistItems, activeIndex]);

  const currentItem = getCurrentContent();

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const extractPlaylistId = (url) => {
    if (!url) return null;
    const match = url.match(/[?&]list=([a-zA-Z0-9_-]+)/);
    return match ? match[1] : null;
  };

  // Auto-advance to next video
  const handleVideoEnd = useCallback(() => {
    let nextIndex = activeIndex + 1;
    if (nextIndex >= playlistItems.length) nextIndex = 0;
    setActiveIndex(nextIndex);
    setStartOffset(0);
    onIndexChange?.(nextIndex);
  }, [activeIndex, playlistItems.length, onIndexChange]);

  // For MP4: force play and prevent pausing
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = mutedRef.current;
    video.play().catch(() => {});
    const onPause = () => { video.play().catch(() => {}); };
    video.addEventListener('pause', onPause);
    return () => video.removeEventListener('pause', onPause);
  }, [activeIndex, currentItem]);

  // Mute/unmute without re-rendering the player
  const handleUnmute = () => {
    mutedRef.current = false;
    setHasUserUnmuted(true);
    // MP4
    if (videoRef.current) videoRef.current.muted = false;
    // YouTube - postMessage to iframe
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'unMute', args: [] }), '*'
      );
    }
    forceRender(n => n + 1); // update icon only
  };

  const toggleMute = () => {
    const newMuted = !mutedRef.current;
    mutedRef.current = newMuted;
    // MP4
    if (videoRef.current) videoRef.current.muted = newMuted;
    // YouTube - postMessage
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: newMuted ? 'mute' : 'unMute', args: [] }), '*'
      );
    }
    if (!newMuted) setHasUserUnmuted(true);
    forceRender(n => n + 1); // update icon only
  };

  const renderPlayer = () => {
    if (!currentItem) {
      if (fallbackVideoUrl) {
        return (
          <video
            ref={videoRef}
            src={fallbackVideoUrl}
            autoPlay muted playsInline loop
            className="w-full h-full object-cover"
            onEnded={handleVideoEnd}
          />
        );
      }
      return (
        <div className="w-full h-full flex items-center justify-center bg-zinc-900">
          <p className="text-zinc-500">No playlist items configured</p>
        </div>
      );
    }

    const { type, url } = currentItem;

    if (type === 'mp4') {
      return (
        <video
          key={`mp4-${activeIndex}`}
          ref={videoRef}
          src={url}
          autoPlay muted playsInline
          className="w-full h-full object-cover"
          onEnded={handleVideoEnd}
        />
      );
    }

    if (type === 'youtube') {
      const videoId = extractYouTubeId(url);
      if (!videoId) return <div className="w-full h-full bg-zinc-900" />;
      // Start param to jump mid-video; always start muted for autoplay, control via postMessage after
      const offset = startOffset > 0 ? `&start=${startOffset}` : '';
      return (
        <div className="w-full h-full">
          <iframe
            key={`yt-${videoId}-${activeIndex}`}
            ref={iframeRef}
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&enablejsapi=1&rel=0&modestbranding=1&controls=0&disablekb=1&playsinline=1${offset}`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="24/7 Player"
          />
        </div>
      );
    }

    if (type === 'youtube_playlist') {
      const playlistId = extractPlaylistId(url);
      if (!playlistId) return <div className="w-full h-full bg-zinc-900" />;
      return (
        <div className="w-full h-full">
          <iframe
            key={`ytpl-${playlistId}`}
            ref={iframeRef}
            src={`https://www.youtube.com/embed/videoseries?list=${playlistId}&autoplay=1&mute=1&loop=1&controls=0&disablekb=1&enablejsapi=1&playsinline=1`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="24/7 Playlist"
          />
        </div>
      );
    }

    return <div className="w-full h-full bg-zinc-900" />;
  };

  // After YouTube iframe loads, unmute it if user already clicked unmute
  useEffect(() => {
    if (!mutedRef.current && iframeRef.current?.contentWindow) {
      // Small delay to let iframe initialize
      const timer = setTimeout(() => {
        iframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'unMute', args: [] }), '*'
        );
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [activeIndex, currentItem]);

  if (isLiveOverriding) return null;

  return (
    <div className="relative aspect-video bg-black overflow-hidden group">
      {renderPlayer()}

      {/* Invisible wall - blocks all click interaction with the video */}
      <div className="absolute inset-0 z-10" data-testid="player-wall" />

      {/* Click to unmute overlay */}
      {mutedRef.current && !hasUserUnmuted && (
        <button
          onClick={handleUnmute}
          className="absolute inset-0 z-30 flex items-center justify-center cursor-pointer"
          data-testid="unmute-overlay"
        >
          <div className="flex items-center gap-2 px-5 py-3 bg-black/80 border border-white/20 text-white text-sm font-medium hover:bg-black/95 transition-colors rounded-sm">
            <VolumeX className="w-5 h-5" />
            Click to unmute
          </div>
        </button>
      )}

      {/* 24/7 Badge */}
      <div className="absolute top-4 left-4 z-20 flex items-center gap-2 pointer-events-none">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white text-xs font-bold uppercase">
          <Radio className="w-3 h-3" />
          24/7
        </div>
        {scheduleEnabled && isInScheduledSlot && scheduledContent?.slotTitle && (
          <div className="flex items-center gap-1.5 px-2 py-1.5 bg-black/70 border border-purple-500/50 text-purple-300 text-[10px] font-bold uppercase">
            <Calendar className="w-3 h-3" />
            {scheduledContent.slotTitle}
          </div>
        )}
        {scheduleEnabled && !isInScheduledSlot && fillGapsWithLoop && (
          <div className="flex items-center gap-1.5 px-2 py-1.5 bg-black/70 border border-zinc-500/50 text-zinc-400 text-[10px] font-bold uppercase">
            <Repeat className="w-3 h-3" />
            Loop
          </div>
        )}
      </div>

      {/* Volume only - on hover */}
      <div className="absolute bottom-3 right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={toggleMute}
          className="p-2.5 bg-black/70 border border-white/10 text-white/80 hover:text-white hover:bg-black/90 transition-colors"
          title={mutedRef.current ? "Unmute" : "Mute"}
          data-testid="volume-toggle"
        >
          {mutedRef.current ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};

export default PlaylistPlayer;
